import React, { useEffect, useContext, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '@/taskpane/contexts/auth/AuthContext';
import { exchangeCodeForToken, exchangeSSOToken, getStudies, storeTokensForPolling } from '@/taskpane/services/sso';
import LoadingScreen from '@/taskpane/components/loading/LoadingScreen';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, Typography } from '@mui/material';
import { decodeCognitoToken } from '@/taskpane/utils/jwt';

/**
 * SSO Callback Page
 * 
 * Handles the OAuth2 callback from GenAPI SSO provider
 * 
 * Flow:
 * 1. Receive authorization code and state from URL params
 * 2. Validate state (CSRF protection)
 * 3. Exchange code for GenAPI access token (single use - prevents reuse)
 * 4. Fetch user's offices from GenAPI
 * 5. If multiple offices: Show inline office selector UI
 *    If single office: Auto-select and proceed
 * 6. Exchange GenAPI token for Cognito tokens
 * 7. Store tokens for cross-context SSO (Desktop Outlook)
 * 8. Redirect to app
 */
interface Office {
  Nom: string;
  NumeroCRPCEN: string;
  TenantId: string;
}

export function SSOCallbackPage() {
  const [searchParams] = useSearchParams();
  const { setTokens, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const hasExecutedRef = useRef(false);
  
  // Office selection state
  const [offices, setOffices] = useState<Office[] | null>(null);
  const [selectedOfficeIndex, setSelectedOfficeIndex] = useState<number | null>(null);
  const [genapiToken, setGenapiToken] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);
  
  // console.log('╔══════════════════════════════════════════════════════════════╗');
  // console.log('║         SSO CALLBACK PAGE - COMPONENT RENDERED               ║');
  // console.log('╚══════════════════════════════════════════════════════════════╝');
  // console.log('[SSO Callback] Render count - hasExecutedRef:', hasExecutedRef.current);
  // console.log('[SSO Callback] URL:', window.location.href);
  // console.log('[SSO Callback] Search params:', window.location.search);
  // console.log('[SSO Callback] Code param:', searchParams.get('code')?.substring(0, 20) + '...');
  // console.log('[SSO Callback] State param:', searchParams.get('state')?.substring(0, 20) + '...');
  // console.log('════════════════════════════════════════════════════════════════');
  
  // Complete authentication with GenAPI token
  const completeAuthentication = async (
    token: string, 
    sessionState: string,
    selectedCrpcen?: string,
    selectedTenantId?: string
  ) => {
    try {
      // console.log('[SSO Callback] 🟢 About to call exchangeSSOToken()...');
      // console.log('[SSO Callback] Selected CRPCEN:', selectedCrpcen);
      // console.log('[SSO Callback] Selected TenantId:', selectedTenantId);
      const cognitoTokens = await exchangeSSOToken(token, selectedCrpcen, selectedTenantId);
      
      // console.log('[SSO Callback] ✅ Successfully obtained Cognito tokens');
      // console.log('[SSO Callback] Cognito IdToken (first 50 chars):', cognitoTokens.IdToken?.substring(0, 50) + '...');
      
      // Store tokens in backend for cross-context retrieval
      try {
        // console.log('[SSO Callback] Storing tokens for cross-context retrieval...');
        await storeTokensForPolling(sessionState!, cognitoTokens);
        // console.log('[SSO Callback] ✓ Tokens stored successfully');
      } catch (error) {
        console.error('[SSO Callback] ✗ Failed to store tokens:', error);
        // Non-fatal error - continue
      }
      
      // Store tokens in context
      setTokens(cognitoTokens);
      
      // Check if there's a saved redirect destination from office switch (tenant_id or crpcen)
      const savedRedirectTenant = localStorage.getItem('redirect_after_tenant_switch');
      const savedRedirectCrpcen = localStorage.getItem('redirect_after_crpcen_switch');
      const savedRedirect = savedRedirectTenant || savedRedirectCrpcen;
      
      let redirectPath = '/folders';
      
      if (savedRedirect) {
        // Remove the leading # if present
        redirectPath = savedRedirect.replace(/^#/, '');
        // Clean up both saved redirects
        localStorage.removeItem('redirect_after_tenant_switch');
        localStorage.removeItem('redirect_after_crpcen_switch');
      }
      
      // Clean up URL
      const baseUrl = window.location.href.split('?')[0].split('#')[0];
      window.history.replaceState(null, '', `${baseUrl}#${redirectPath}`);
      
      // Success notification
      enqueueSnackbar('Connexion SSO réussie', { variant: 'success' });
      
      // Redirect to intended destination
      navigate(redirectPath, { replace: true });
      
    } catch (error: any) {
      console.error('[SSO Callback] Failed to exchange SSO token:', error);
      enqueueSnackbar('Erreur lors de la création de la session', { variant: 'error' });
      setError('Erreur d\'échange de token');
      setIsProcessing(false);
    }
  };
  
  // Handle office selection validation
  const handleValidateOffice = async () => {
    if (selectedOfficeIndex === null || !offices || !genapiToken || !state) {
      enqueueSnackbar('Veuillez sélectionner une étude', { variant: 'warning' });
      return;
    }
    
    const selectedOffice = offices[selectedOfficeIndex];
    // console.log('[SSO Callback] User selected office:', {
    //   name: selectedOffice.Nom,
    //   crpcen: selectedOffice.NumeroCRPCEN,
    //   tenantId: selectedOffice.TenantId
    // });
    
    // Store selected office
    localStorage.setItem('selected_office_tenant_id', selectedOffice.TenantId);
    localStorage.setItem('selected_office_name', selectedOffice.Nom);
    // Store CRPCEN if available (some studies may not have it yet)
    localStorage.setItem('selected_office_crpcen', selectedOffice.NumeroCRPCEN || '');
    
    // Continue with authentication - pass selected office info to backend
    // Backend will handle case where CRPCEN is empty by using TenantId
    setIsProcessing(true);
    await completeAuthentication(
      genapiToken, 
      state,
      selectedOffice.NumeroCRPCEN || undefined,  // selected CRPCEN (may be empty)
      selectedOffice.TenantId                      // selected TenantId (always present)
    );
  };
  
  // Reset everything and restart SSO process
  const handleReset = () => {
    console.log('[SSO Callback] Resetting SSO state and returning to login');
    
    // Clear all tokens and auth state
    logout();
    
    // Clear SSO-related session storage
    sessionStorage.removeItem('sso_state');
    sessionStorage.removeItem('sso_code_verifier');
    sessionStorage.removeItem('sso_redirect_uri');
    
    // Set a flag to prevent callback redirect loop
    sessionStorage.setItem('sso_reset', 'true');
    
    // Clear any error state
    setError(null);
    setIsProcessing(false);
    
    // Clean URL by removing query parameters
    // Use window.location to fully reset the URL without query params
    const baseUrl = window.location.href.split('?')[0].split('#')[0];
    window.location.href = `${baseUrl}#/login`;
  };
  
  useEffect(() => {
    // Prevent double execution (React StrictMode can cause this in development)
    if (hasExecutedRef.current) {
      // console.log('⚠️⚠️⚠️ [SSO Callback] Callback already executed, skipping duplicate execution ⚠️⚠️⚠️');
      // console.log('[SSO Callback] This indicates React StrictMode double-render or component remount');
      return;
    }
    
    // console.log('[SSO Callback] 🚀 Setting hasExecutedRef to true and starting handleCallback');
    hasExecutedRef.current = true;
    handleCallback();
  }, []);
  
  const handleCallback = async () => {
    try {
      // console.log('[SSO Callback] ==================== CALLBACK STARTED ====================');
      // console.log('[SSO Callback] Processing SSO callback...');
      // console.log('[SSO Callback] Full URL:', window.location.href);
      
      // Get URL parameters
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      
      // console.log('[SSO Callback] URL Parameters:', {
      //   code: code ? code.substring(0, 20) + '...' : 'MISSING',
      //   state: state || 'MISSING',
      //   stateTruncated: state ? state.substring(0, 10) + '...' : 'N/A',
      //   error: errorParam || 'none'
      // });
      
      // Check if this matches the expected session ID (for Desktop Outlook cross-context SSO)
      const expectedSessionId = localStorage.getItem('sso_session_id');
      const storedSsoState = localStorage.getItem('sso_state');
      
      // console.log('[SSO Callback] Checking localStorage for session tracking:', {
      //   sso_session_id: expectedSessionId ? expectedSessionId.substring(0, 10) + '...' : 'NOT FOUND',
      //   sso_state: storedSsoState ? storedSsoState.substring(0, 10) + '...' : 'NOT FOUND',
      //   sso_session_id_full: expectedSessionId || 'NOT FOUND',
      //   sso_state_full: storedSsoState || 'NOT FOUND'
      // });
      
      if (expectedSessionId) {
        // console.log('[SSO Callback] ========== CROSS-CONTEXT SSO DETECTED ==========');
        // console.log('[SSO Callback] Desktop Outlook cross-context SSO flow');
        // console.log('[SSO Callback] Comparing session IDs:');
        // console.log('[SSO Callback]   - Received state from URL:', state);
        // console.log('[SSO Callback]   - Expected session ID (from add-in):', expectedSessionId);
        // console.log('[SSO Callback]   - Match:', state === expectedSessionId ? '✓ YES - SESSION IDS MATCH!' : '✗ NO - MISMATCH DETECTED!');
        
          if (state !== expectedSessionId) {
            console.error('[SSO Callback] Critical state mismatch - Received:', state?.substring(0, 10), 'Expected:', expectedSessionId.substring(0, 10));
          }
        // else {
        //   console.log('[SSO Callback] ✓ State matches! Tokens will be stored correctly.');
        //   console.log('[SSO Callback] Add-in polling endpoint: /auth/sso/poll-tokens/' + state);
        // }
      }
      // else {
      //   console.log('[SSO Callback] Standard SSO flow (Web/Mobile) - no cross-context session tracking');
      // }
      
      // Check for errors from SSO provider
      if (errorParam) {
        const message = errorDescription || errorParam;
        console.error('[SSO Callback] Error from SSO provider:', message);
        enqueueSnackbar(`Erreur d'authentification: ${message}`, { variant: 'error' });
        setError(message);
        setIsProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }
      
      // Validate required parameters
      if (!code || !state) {
        console.error('[SSO Callback] Missing code or state parameter');
        enqueueSnackbar('Paramètres manquants dans le callback SSO', { variant: 'error' });
        setError('Paramètres manquants');
        setIsProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }
      
      // console.log('[SSO Callback] Code and state received, proceeding with exchange');
      
      // Exchange authorization code for GenAPI access token
      let genapiToken: string;
      try {
        // console.log('[SSO Callback] 🔵 About to call exchangeCodeForToken()...');
        genapiToken = await exchangeCodeForToken(code, state);
        // console.log('[SSO Callback] ✅ Successfully exchanged code for GenAPI token');
        // console.log('[SSO Callback] GenAPI token received (first 50 chars):', genapiToken.substring(0, 50) + '...');
      } catch (error: any) {
        console.error('[SSO Callback] Failed to exchange code:', error);
        
        // Check if it's because backend endpoint is not configured
        if (error.message?.includes('Backend token exchange endpoint not configured')) {
          enqueueSnackbar(
            'Configuration SSO manquante - Veuillez contacter l\'administrateur',
            { variant: 'error', persist: true }
          );
          setError('Configuration SSO incomplète');
        } else {
          enqueueSnackbar('Erreur lors de l\'échange du code d\'autorisation', { variant: 'error' });
          setError('Erreur d\'échange de code');
        }
        
        setIsProcessing(false);
        setTimeout(() => navigate('/login'), 5000);
        return;
      }
      
      // Check if user is switching to a specific office (via tenant_id or crpcen)
      const targetTenantId = localStorage.getItem('target_office_tenant_id');
      const targetCRPCEN = localStorage.getItem('target_office_crpcen');
      const targetOfficeName = localStorage.getItem('target_office_name') || 'Étude sélectionnée';

      // Get user's offices from GenAPI
      let fetchedOffices;
      try {
        fetchedOffices = await getStudies(genapiToken);
        
        // Store available offices for the account menu (always store, even if just 1 office)
        if (fetchedOffices && fetchedOffices.length > 0) {
          localStorage.setItem('available_offices', JSON.stringify(fetchedOffices));
        }
        
        if (targetTenantId && fetchedOffices && fetchedOffices.length > 0) {
          // Find the target office in the list (prioritize tenant_id, fallback to crpcen)
          const targetOffice = fetchedOffices.find(o => 
            o.TenantId === targetTenantId || 
            (targetCRPCEN && o.NumeroCRPCEN === targetCRPCEN)
          );
          
          if (targetOffice) {
            // Update selected office
            localStorage.setItem('selected_office_tenant_id', targetOffice.TenantId);
            localStorage.setItem('selected_office_name', targetOffice.Nom);
            localStorage.setItem('selected_office_crpcen', targetOffice.NumeroCRPCEN || '');
            
            // Clear target office flags
            localStorage.removeItem('target_office_tenant_id');
            localStorage.removeItem('target_office_name');
            localStorage.removeItem('target_office_crpcen');
            sessionStorage.removeItem('sso_auto_triggered');
            
            // Proceed with authentication for target office
            await completeAuthentication(
              genapiToken, 
              state, 
              targetOffice.NumeroCRPCEN || undefined, 
              targetOffice.TenantId
            );
            return;
          } else {
            console.warn('[SSO Callback] Target office not found in user offices');
            localStorage.setItem('selected_office_tenant_id', targetTenantId);
            localStorage.setItem('selected_office_name', targetOfficeName);
            if (targetCRPCEN) {
              localStorage.setItem('selected_office_crpcen', targetCRPCEN);
            } else {
              localStorage.removeItem('selected_office_crpcen');
            }
            localStorage.removeItem('target_office_tenant_id');
            localStorage.removeItem('target_office_name');
            localStorage.removeItem('target_office_crpcen');
            sessionStorage.removeItem('sso_auto_triggered');

            await completeAuthentication(
              genapiToken,
              state,
              targetCRPCEN || undefined,
              targetTenantId
            );
            return;
          }
        }
        
        if (fetchedOffices && fetchedOffices.length > 1) {
          // Multiple offices - show selector UI
          // console.log('[SSO Callback] Multiple offices found, showing selector');
          setOffices(fetchedOffices);
          setGenapiToken(genapiToken);
          setState(state);
          setIsProcessing(false);
          return; // Stop here and wait for user selection
        }
        
        // Single office - auto-select and proceed
        if (fetchedOffices && fetchedOffices.length > 0) {
          const firstOffice = fetchedOffices[0];
          // console.log('[SSO Callback] Single office, auto-selecting:', {
          //   name: firstOffice.Nom,
          //   crpcen: firstOffice.NumeroCRPCEN,
          //   tenantId: firstOffice.TenantId
          // });
          localStorage.setItem('selected_office_tenant_id', firstOffice.TenantId);
          localStorage.setItem('selected_office_name', firstOffice.Nom);
          localStorage.setItem('selected_office_crpcen', firstOffice.NumeroCRPCEN || '');
          
          // Proceed with token exchange with selected office info
          // console.log('[SSO Callback] Proceeding to exchange for Cognito tokens with selected office');
          await completeAuthentication(
            genapiToken, 
            state, 
            firstOffice.NumeroCRPCEN || undefined, 
            firstOffice.TenantId
          );
          return;
        }
      } catch (error) {
        console.warn('[SSO Callback] Failed to fetch offices (non-fatal):', error);
        if (targetTenantId) {
          localStorage.setItem('selected_office_tenant_id', targetTenantId);
          localStorage.setItem('selected_office_name', targetOfficeName);
          if (targetCRPCEN) {
            localStorage.setItem('selected_office_crpcen', targetCRPCEN);
          } else {
            localStorage.removeItem('selected_office_crpcen');
          }
          localStorage.removeItem('target_office_tenant_id');
          localStorage.removeItem('target_office_name');
          localStorage.removeItem('target_office_crpcen');
          sessionStorage.removeItem('sso_auto_triggered');

          await completeAuthentication(
            genapiToken,
            state,
            targetCRPCEN || undefined,
            targetTenantId
          );
          return;
        }
        // Continue anyway - backend will handle office selection from GenAPI token
      }
      
      // Proceed with token exchange (no office selection available)
      // console.log('[SSO Callback] Proceeding to exchange for Cognito tokens');
      await completeAuthentication(genapiToken, state);
      
    } catch (error: any) {
      console.error('[SSO Callback] Unexpected error:', error);
      enqueueSnackbar('Erreur inattendue lors de la connexion SSO', { variant: 'error' });
      setError('Erreur inattendue');
      setIsProcessing(false);
      setTimeout(() => navigate('/login'), 3000);
    }
  };
  
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          height: '100vh',
          textAlign: 'center',
        }}
      >
        <Typography color="error" variant="h6" gutterBottom>
          Erreur de connexion SSO
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Cliquez sur le bouton ci-dessous pour réessayer
        </Typography>
        <Button
          variant="contained"
          onClick={handleReset}
        >
          Revenir à la page de connexion
        </Button>
      </Box>
    );
  }
  
  if (isProcessing) {
    return (
      <LoadingScreen 
        message="Connexion en cours..."
        submessage="Veuillez patienter pendant que nous vérifions vos informations"
      />
    );
  }
  
  // Show office selector if multiple offices
  if (offices && offices.length > 1) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          minHeight: '100vh',
          backgroundColor: 'transparent',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'transparent',
            borderRadius: 2,
            p: 4,
            maxWidth: 600,
            width: '100%',
            boxShadow: 'none',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
            Sélectionnez votre étude
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Vous compte est relié à plusieurs études. Veuillez sélectionner celle que vous souhaitez utiliser.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {offices.map((office, index) => (
              <Button
                key={index}
                onClick={() => setSelectedOfficeIndex(index)}
                sx={{
                  border: '2px solid',
                  borderColor: selectedOfficeIndex === index ? '#1976d2' : '#e0e0e0',
                  backgroundColor: selectedOfficeIndex === index ? '#e3f2fd' : 'white',
                  color: selectedOfficeIndex === index ? '#1976d2' : '#333',
                  padding: 2,
                  textAlign: 'left',
                  textTransform: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: selectedOfficeIndex === index ? '#bbdefb' : '#f5f5f5',
                    borderColor: '#1976d2',
                  },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {office.Nom}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                   CRPCEN: {office.NumeroCRPCEN}
                </Typography>
              </Button>
            ))}
          </Box>
          
          <Button
            variant="contained"
            fullWidth
            onClick={handleValidateOffice}
            disabled={selectedOfficeIndex === null}
            sx={{
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            Valider
          </Button>
        </Box>
      </Box>
    );
  }
  
  return <LoadingScreen />;
}
